/**
 * Auth events between this browser's tabs. Signing out in one tab signs out every tab
 *: the browser has one sign-in.
 * Without BroadcastChannel (very old Safari) every call is a no-op: no banner appears, and the
 * other tabs stay signed in until their session token runs out.
 */

export type AuthEvent = { type: 'signed-out'; userId: string };

const CHANNEL_NAME = 'software-factory-auth';

let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel === undefined) {
    try {
      channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CHANNEL_NAME);
    } catch {
      channel = null;
    }
  }
  return channel;
}

/** Closes this tab's channel; the next call opens a new one. Tests use it so Jest can exit. */
export function closeAuthChannel(): void {
  channel?.close();
  channel = undefined;
}

export function isAuthEvent(value: unknown): value is AuthEvent {
  if (!value || typeof value !== 'object') return false;
  const e = value as Record<string, unknown>;
  if (typeof e.userId !== 'string') return false;
  return e.type === 'signed-out';
}

/** Tells the other tabs. The posting tab never hears its own message. */
export function postAuthEvent(event: AuthEvent): void {
  try {
    getChannel()?.postMessage(event);
  } catch {
    // A closed or unavailable channel: the other tabs just aren't told.
  }
}

/** Subscribes to the other tabs' events; returns the unsubscribe. */
export function onAuthEvent(listener: (event: AuthEvent) => void): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const handle = (message: MessageEvent) => {
    if (isAuthEvent(message.data)) listener(message.data);
  };
  ch.addEventListener('message', handle);
  return () => ch.removeEventListener('message', handle);
}

/**
 * Whether `event` signs this tab out: any sign-out while this tab is signed in, whoever it was,
 * because the browser's one sign-in cookie is gone either way.
 */
export function signsOutThisTab(event: AuthEvent, tab: { userId?: string | null }): boolean {
  return event.type === 'signed-out' && Boolean(tab.userId);
}
